import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faClock, faCoins } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Modal } from "@/components/Modals/Modal";

export interface EnrichAnywayModalProps {
	isOpened: boolean;
	/** Number of title-excluded leads being recovered. */
	count: number;
	/** The affected job titles (whitelist candidates). */
	titles: string[];
	isLoading?: boolean;
	onConfirm: (whitelist: boolean) => void;
	onClose: () => void;
}

const summarizeTitles = (titles: string[]): string => {
	const shown = titles.slice(0, 2).join(", ");

	return titles.length > 2 ? `${shown} +${titles.length - 2}` : shown;
};

/**
 * "Enrich anyway" confirmation (PREC-3536 Phase 5): 0.9-credit discount callout, the
 * whitelist-these-titles checkbox (default ON) and the 1–2-minute timing note.
 */
export const EnrichAnywayModal: FunctionComponent<EnrichAnywayModalProps> = ({
	isOpened,
	count,
	titles,
	isLoading,
	onConfirm,
	onClose,
}) => {
	const { t } = useTranslation();
	const [whitelist, setWhitelist] = useState(true);

	// Whitelisting defaults to ON for every new recovery.
	useEffect(() => {
		if (isOpened) {
			setWhitelist(true);
		}
	}, [isOpened]);

	// The shared Modal keeps its dialog mounted when closed; unmount fully instead.
	if (!isOpened) {
		return null;
	}

	return (
		<Modal
			cancelButtonText={t("inbox.enrichAnywayFlow.cancel")}
			handleClose={onClose}
			handleSave={() => onConfirm(whitelist)}
			isLoading={isLoading}
			isOpened={isOpened}
			size="sm"
			submitButtonText={t("inbox.enrichAnywayFlow.confirm", { count })}
			title={t("inbox.enrichAnywayFlow.modalTitle", { count })}
		>
			<div className="flex flex-col gap-4" data-test="enrich-anyway-modal">
				<p className="text-sm text-text-meta">{t("inbox.enrichAnywayFlow.modalLead")}</p>

				<div
					className="flex items-start gap-2 rounded-lg bg-status-warning-light p-3 text-sm"
					data-test="enrich-anyway-cost"
				>
					<Icon className="mt-0.5 h-4 w-4 text-status-warning" icon={faCoins} />
					<span>
						{t("inbox.enrichAnywayFlow.costCallout", { total: (count * 0.9).toFixed(1) })}
					</span>
				</div>

				<div className="rounded-lg border border-gray-200 p-3" data-test="enrich-anyway-whitelist">
					<Checkbox
						isChecked={whitelist}
						labelClass="cursor-pointer"
						testId="enrich-anyway-whitelist-checkbox"
						onChange={() => setWhitelist(!whitelist)}
					>
						<div className="flex flex-col">
							<span className="text-sm font-semibold">
								{t("inbox.enrichAnywayFlow.whitelistTitle")}
							</span>
							<span className="text-xs text-text-meta">
								{t("inbox.enrichAnywayFlow.whitelistDescription", {
									titles: summarizeTitles(titles),
								})}
							</span>
						</div>
					</Checkbox>
				</div>

				<div className="flex items-start gap-2 text-xs text-text-meta">
					<Icon className="mt-0.5 h-3.5 w-3.5" icon={faClock} />
					<span>{t("inbox.enrichAnywayFlow.timingNote")}</span>
				</div>
			</div>
		</Modal>
	);
};
